import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEdit,
  FaTh,
  FaList,
  FaTrash,
  FaPrint,
  FaUtensils,
  FaBox,
  FaLaptop,
  FaShoppingBag,
  FaClock,
  FaUser,
  FaCalendarAlt
} from "react-icons/fa";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { useStatuses } from "../../hooks/useStatuses";
import {
  showCurrencySymbol,
  showTranslatedValue,
  formatTime12Hour,
} from "../../helper/generalHelper";
import ReceiptModal from "../../components/restaurant/Order/PrintBill";
import ChangeStatusButton from "../../components/restaurant/Order/ChangeStatusButton";
import TableSearch from "../../components/common/TableSearch";
import Pagination from "../../components/common/Pagination";
import { usePagination } from "../../hooks/usePagination";

const orderTypeTabs = [
  { key: "all", label: "All Orders", icon: FaUtensils, color: "gray" },
  { key: "table", label: "Dine In", icon: FaUtensils, color: "blue" },
  { key: "takeaway", label: "Takeaway", icon: FaShoppingBag, color: "green" },
  { key: "QSR", label: "QSR", icon: FaBox, color: "purple" },
];

const Dispatch = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { statusesLoading, findStatus } = useStatuses();
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("dispatch_active_tab") || "all");
  const [timeFilter, setTimeFilter] = useState(() => localStorage.getItem("dispatch_time_filter") || "today");
  const [startDate, setStartDate] = useState(() => localStorage.getItem("dispatch_start_date") || "");
  const [endDate, setEndDate] = useState(() => localStorage.getItem("dispatch_end_date") || "");

  useEffect(() => {
    localStorage.setItem("dispatch_active_tab", activeTab);
    localStorage.setItem("dispatch_time_filter", timeFilter);
    localStorage.setItem("dispatch_start_date", startDate);
    localStorage.setItem("dispatch_end_date", endDate);
  }, [activeTab, timeFilter, startDate, endDate]);
  const [timeAgo, setTimeAgo] = useState({});

  const filterByDate = (ordersList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return ordersList.filter(order => {
      if (!order.order_date) return true; // Keep orders without dates just in case
      
      const orderDate = new Date(order.order_date);
      orderDate.setHours(0, 0, 0, 0);

      const diffTime = Math.abs(today - orderDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      switch (timeFilter) {
        case "today":
          return today.getTime() === orderDate.getTime();
        case "yesterday":
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return yesterday.getTime() === orderDate.getTime();
        case "last_7_days":
          return diffDays <= 7;
        case "last_30_days":
          return diffDays <= 30;
        case "custom":
          if (startDate && endDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return orderDate.getTime() >= start.getTime() && orderDate.getTime() <= end.getTime();
          }
          return true;
        case "all":
        default:
          return true;
      }
    });
  };

  const filteredOrdersByTab = filterByDate(orders).filter(
    (order) => activeTab === "all" || order.order_type === activeTab
  );

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const response = await api.put(`${apiPrefix}/kot/order-status-update`, {
        order_id: orderId,
        status_id: newStatus,
      });
      if (response.success) {
        toast.success("Order status updated.");
        fetchOrders(); // Refresh orders after successful update
      } else {
        toast.error(response.message || "Update failed.");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Failed to update status.");
    }
  };

  const handlePrintClick = (order) => {
    setSelectedOrder(order);
    setIsPrintOpen(true);
  };

  useEffect(() => {
    if (statusesLoading === false) {
      fetchOrders();
    }
  }, [statusesLoading]);

  useEffect(() => {
    // Update time ago every minute
    const interval = setInterval(() => {
      const newTimeAgo = {};
      orders.forEach(order => {
        const orderTime = new Date(`${order.order_date}T${order.order_time}`);
        newTimeAgo[order.id] = getTimeAgo(orderTime);
      });
      setTimeAgo(newTimeAgo);
    }, 60000);

    return () => clearInterval(interval);
  }, [orders]);

  const getTimeAgo = (orderTime) => {
    const now = new Date();
    const diffMs = now - orderTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${apiPrefix}/order/all`);
      const ordersData = response.data || [];
      
      // Initialize time ago
      const initialTimeAgo = {};
      ordersData.forEach(order => {
        const orderTime = new Date(`${order.order_date}T${order.order_time}`);
        initialTimeAgo[order.id] = getTimeAgo(orderTime);
      });
      setTimeAgo(initialTimeAgo);
      
      setOrders(ordersData);
    } catch (err) {
      console.error("Error fetching orders:", err);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (order) => {
    navigate(`/${apiPrefix}/orders/edit/${order.id}`);
  };

  const handleDeleteClick = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      const response = await api.delete(`${apiPrefix}/order/delete?id=${orderId}`);
      setOrders((prev) => prev.filter((order) => order.id !== orderId));
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order.");
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-blue-100 text-blue-800 border-blue-200",
      preparing: "bg-orange-100 text-orange-800 border-orange-200",
      ready: "bg-green-100 text-green-800 border-green-200",
      completed: "bg-gray-100 text-gray-800 border-gray-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getOrderTypeColor = (type) => {
    const typeColors = {
      table: "bg-blue-50 text-blue-700 border-blue-200",
      takeaway: "bg-green-50 text-green-700 border-green-200",
      QSR: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return typeColors[type] || "bg-gray-50 text-gray-700 border-gray-200";
  };

  if (loading || statusesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading kitchen orders...</p>
        </div>
      </div>
    );
  }

  // Filter orders into columns
  const readyOrders = filteredOrdersByTab.filter(o => o.order_status == 4);
  const outOrders = filteredOrdersByTab.filter(o => o.order_status == 6);
  const completedOrders = filteredOrdersByTab.filter(o => o.order_status == 10);

  const OrderCard = ({ order, nextStatus, nextStatusLabel, prevStatus, prevStatusLabel }) => {
    const orderTime = new Date(`${order.order_date}T${order.order_time}`);
    const shortDate = order.order_date ? order.order_date.replace(/-/g, '') : '';
    const shortId = order.daily_order_number 
        ? `${shortDate}-${String(order.daily_order_number).padStart(3, '0')}`
        : order.id.split('-')[0].toUpperCase();

    return (
      <div 
        draggable={true}
        onDragStart={(e) => {
          e.dataTransfer.setData("orderId", order.id);
          e.target.style.opacity = "0.4";
        }}
        onDragEnd={(e) => {
          e.target.style.opacity = "1";
        }}
        className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-all mb-4 cursor-grab active:cursor-grabbing"
      >
        <div className="flex justify-between items-start mb-2">
          <span className="font-bold text-gray-900">
            #{shortId}
          </span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <FaClock className="text-[10px]" />
            <span>{timeAgo[order.id] || getTimeAgo(orderTime)}</span>
          </div>
        </div>
        
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-800">
            Customer: {order.customer_name?.trim() || "Guest Customer"}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${getOrderTypeColor(order.order_type)}`}>
              {order.order_type === "table" 
                ? `Table ${order.table_name || order.table_number || ""}` 
                : order.order_type.replace("_", " ")}
            </span>
          </div>
        </div>

        <div className="space-y-1 mb-4 border-t pt-2">
          {order.order_details?.map((item) => {
            const { menu } = item;
            return (
              <div key={item.id} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate mr-2">
                  <span className="font-medium text-blue-600">{item.quantity}x</span> {showTranslatedValue(menu, "title") || `Item`}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 w-full mt-4">
          {prevStatus && (
            <button
              onClick={() => handleStatusUpdate(order.id, prevStatus)}
              className="flex-1 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-medium rounded-lg border border-red-200 transition-colors text-xs flex items-center justify-center gap-1"
            >
              ◀ {prevStatusLabel}
            </button>
          )}
          {nextStatus ? (
            <button
              onClick={() => handleStatusUpdate(order.id, nextStatus)}
              className="flex-1 py-2 bg-green-50 hover:bg-green-100 text-green-700 font-medium rounded-lg border border-green-200 transition-colors text-xs flex items-center justify-center gap-1"
            >
              {nextStatusLabel} {prevStatus && "▶"}
            </button>
          ) : (
            <div className="flex-1 py-2 bg-green-50 text-green-600 font-bold rounded-lg border border-green-200 text-center text-xs flex items-center justify-center">
              Delivered Successfully
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaArrowLeft className="text-lg text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Delivery / Dispatch Board
                </h1>
                <p className="text-gray-600 mt-1">Manage and track order deliveries</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {timeFilter === "custom" && (
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1 shadow-sm">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-transparent border-none text-xs font-medium text-gray-700 focus:ring-0 outline-none w-28 p-1 cursor-pointer"
                  />
                  <span className="text-gray-400 text-xs font-medium px-1">-</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-transparent border-none text-xs font-medium text-gray-700 focus:ring-0 outline-none w-28 p-1 cursor-pointer"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                <FaCalendarAlt className="text-gray-400 text-sm" />
                <select 
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="bg-transparent border-none text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer outline-none"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="custom">Custom Date</option>
                  <option value="all">All Time</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-3 mb-6">
          {orderTypeTabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-200 font-medium text-sm ${
                  activeTab === tab.key
                    ? `bg-${tab.color}-50 border-${tab.color}-200 text-${tab.color}-700 shadow-sm`
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <IconComponent className={`text-${tab.color}-500`} />
                {tab.label}
                <span className="bg-gray-100 text-gray-600 rounded-full px-2 py-1 text-xs ml-1">
                  {tab.key === "all" 
                    ? filterByDate(orders).length 
                    : filterByDate(orders).filter(o => o.order_type === tab.key).length
                  }
                </span>
              </button>
            );
          })}
        </div>

        {/* Kanban Board Columns */}
        <div className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 gap-6 pb-4 scroll-smooth">
          {/* Column 1: Ready for Delivery */}
          <div 
            className="bg-gray-100/50 rounded-xl p-4 border border-gray-200 h-[calc(100vh-150px)] overflow-y-auto min-w-[85vw] md:min-w-0 snap-center shrink-0"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const orderId = e.dataTransfer.getData("orderId");
              if (orderId) handleStatusUpdate(orderId, 4);
            }}
          >
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-100/90 py-2 z-10 backdrop-blur-sm">
              <h2 className="font-bold text-gray-800 text-lg">Ready for Delivery</h2>
              <span className="bg-white text-gray-600 rounded-full px-3 py-1 text-xs font-bold border shadow-sm">
                {readyOrders.length}
              </span>
            </div>
            {readyOrders.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No orders ready for delivery</div>
            ) : (
              readyOrders.map((order) => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  prevStatus={3}
                  prevStatusLabel="Kitchen"
                  nextStatus={10} 
                  nextStatusLabel="Deliver" 
                />
              ))
            )}
          </div>

          {/* Column 3: Success */}
          <div 
            className="bg-gray-100/50 rounded-xl p-4 border border-gray-200 h-[calc(100vh-150px)] overflow-y-auto min-w-[85vw] md:min-w-0 snap-center shrink-0"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const orderId = e.dataTransfer.getData("orderId");
              if (orderId) handleStatusUpdate(orderId, 10);
            }}
          >
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-100/90 py-2 z-10 backdrop-blur-sm">
              <h2 className="font-bold text-gray-800 text-lg">Success</h2>
              <span className="bg-white text-gray-600 rounded-full px-3 py-1 text-xs font-bold border shadow-sm">
                {completedOrders.length}
              </span>
            </div>
            {completedOrders.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No completed deliveries</div>
            ) : (
              completedOrders.map((order) => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  prevStatus={4}
                  prevStatusLabel="Undo"
                  nextStatus={null} 
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Print Modal */}
      {isPrintOpen && selectedOrder && (
        <ReceiptModal
          open={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          orderData={selectedOrder}
        />
      )}
    </div>
  );
};

export default Dispatch;